<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { modalManagerToken } from "@/tokens";
    import ProductEmptyIcon from "@apps/gift_registry/components/icons/ProductEmptyIcon.svelte";
    import { REGISTRY_FORM_MODAL_ACTION_TYPES } from "@apps/gift_registry/constants";
    import { RegistryFormModalWidgetDefinition } from "@apps/gift_registry/definitions";
    import GiftRegistryQuery from "@apps/gift_registry/queries/GiftRegistryQuery";
    import GiftRegistryService from "@apps/gift_registry/services/GiftRegistryService";
    import GiftRegistryStorefrontValuesService from "@apps/gift_registry/services/GiftRegistryStorefrontValuesService";
    import { registryFormModalToken } from "@apps/gift_registry/tokens";
    import WishlistPageListSkeleton from "@apps/wishlist/widgets/wishlist_page_widget/components/WishlistPageListSkeleton.svelte";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import { CommonModule } from "@modules/common_module/CommonModule";
    import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import { lastValueFrom, map } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import Button from "@components/Buttons/Button.svelte";
    import EmptyState from "@components/EmptyState/EmptyState.svelte";
    import Icon from "@components/Icon.svelte";
    import PlusIcon from "@components/icons/PlusIcon.svelte";
    import T from "@components/T.svelte";

    import LoginSection from "../add_to_registry_modal_widget/components/LoginSection.svelte";
    import GiftRegistryPageList from "./components/GiftRegistryPageList.svelte";

    export let giftRegistryService: GiftRegistryService;
    export let giftRegistryStorefrontValuesService: GiftRegistryStorefrontValuesService;
    export let giftRegistryQuery: GiftRegistryQuery;
    export let productsQuery: ProductsQuery;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let showTitle: boolean;
    export let afterWidgetInject: () => void;

    const status$ = giftRegistryQuery.status$;
    const registries$ = giftRegistryQuery.registries$;

    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    const modalManager = container.resolve(modalManagerToken);
    const dynamicImportManager =
        container.resolve(DynamicImportModule).exports.dynamicImportManager;
    const urlsBuilder = container.resolve(CommonModule).exports.urlsBuilder;

    const i18nModule = container.resolve(I18nModule);

    const empty_state_title$ = i18nModule.exports.i18n.selectT(
        "giftRegistry.GiftRegistryPageWidget",
        "empty_state_title"
    );

    const handleOpenRegistryFormModal = async () => {
        await dynamicImportManager.fetch({
            definition: RegistryFormModalWidgetDefinition,
        });
        // Ensure the modal widget initializes asynchronously before opening it
        setTimeout(() => {
            modalManager.openModal(registryFormModalToken, true, {
                actionType: REGISTRY_FORM_MODAL_ACTION_TYPES.create,
            });
        }, 0);
    };

    const handleDeleteRegistry = (e: CustomEvent) => {
        lastValueFrom(giftRegistryService.deleteGiftRegistry(e.detail));
    };

    const handleDeleteItem = (e: CustomEvent) => {
        lastValueFrom(giftRegistryService.deleteGiftRegistryItem(e.detail));
    };

    const handleMoveItem = (e: CustomEvent) => {
        lastValueFrom(giftRegistryService.moveGiftRegistryItem(e.detail));
    };

    const handleUpdateQuantity = (e: CustomEvent) => {
        lastValueFrom(giftRegistryService.updateGiftRegistryItem(e.detail));
    };

    onMount(() => {
        if ($isCustomer$) {
            lastValueFrom(giftRegistryService.getGiftRegistriesWithItems());
        }

        afterWidgetInject();
    });
</script>

<div class="gw-gr-page__wrapper">
    <div class="gw-gr-page">
        {#if $isCustomer$}
            {#if $registries$.length}
                <div class="gw-gr-page__header">
                    {#if showTitle}
                        <h3 class="gw-gr-page__header-title">
                            <T
                                namespace="giftRegistry.GiftRegistryPageWidget"
                                translationKey="header_title"
                            />
                        </h3>
                    {/if}
                    <div class="gw-gr-page__create-btn-wrapper">
                        <Button
                            variant="outlined"
                            on:click={handleOpenRegistryFormModal}
                            size="large"
                            rounded
                        >
                            <slot name="icon">
                                <Icon width={10} height={10} color="#777E90">
                                    <PlusIcon />
                                </Icon>
                            </slot>
                            <T
                                namespace="giftRegistry.GiftRegistryPageWidget"
                                translationKey="create_new_registry"
                            />
                        </Button>
                    </div>
                </div>
            {/if}

            {#if [STATUS.INITIAL_LOADING, STATUS.LOADING].includes($status$)}
                <WishlistPageListSkeleton />
            {:else if !$registries$.length}
                <EmptyState title={$empty_state_title$}>
                    <svelte:fragment slot="icon">
                        <ProductEmptyIcon />
                    </svelte:fragment>
                    <Button
                        on:click={handleOpenRegistryFormModal}
                        variant="link"
                        size="medium"
                        slot="footer"
                        rounded
                    >
                        <slot name="icon">
                            <Icon width={10} height={10} color="#777E90">
                                <PlusIcon />
                            </Icon>
                        </slot>
                        <T
                            namespace="giftRegistry.GiftRegistryPageWidget"
                            translationKey="create_new_registry"
                        />
                    </Button>
                </EmptyState>
            {:else}
                {#each $registries$ as registry (registry.id)}
                    <GiftRegistryPageList
                        on:deleteRegistry={handleDeleteRegistry}
                        on:deleteItem={handleDeleteItem}
                        on:moveItem={handleMoveItem}
                        on:updateQuantity={handleUpdateQuantity}
                        registries={$registries$}
                        {registry}
                        {productsQuery}
                        {giftRegistryService}
                        {giftRegistryQuery}
                        {dynamicImportManager}
                        {giftRegistryStorefrontValuesService}
                        namespace="giftRegistry.GiftRegistryPageWidget"
                    />
                {/each}
            {/if}
        {:else}
            <div class="gw-gr-page__login-section">
                <LoginSection
                    {urlsBuilder}
                    namespace="giftRegistry.GiftRegistryPageWidget"
                />
            </div>
        {/if}
    </div>
</div>

<style lang="postcss" global>
    @import "../../../../themes/globals.css";
    @import "../../../../themes/default/default.css";

    .gw-gr-page {
        --gw-button__main-color: var(--gw-gr-theme-primaryColor);
        --gw-button__text-color: #fff;

        max-width: 1240px;
        margin: 0 auto;

        &__wrapper {
            margin-left: calc(-50vw + 50%);
            margin-right: calc(-50vw + 50%);
            padding: 0 40px;

            @media all and (max-width: 700px) {
                margin-right: 0;
                margin-left: 0;
                padding: 0 20px;
            }

            @media all and (max-width: 500px) {
                padding: 0;
            }
        }

        &__header-title {
            padding: 0 0 1.25rem 0;
            font-size: 22px;
            margin: 0 1.6rem 0 0;
            font-weight: 600;
        }

        &__create-btn-wrapper {
            padding: 1em 0;

            .gw-btn.gw-button.--outlined {
                color: var(--gw-theme-main-text-primary);
                font-weight: 700;

                &:hover,
                &:focus,
                &:focus-within {
                    opacity: 0.8;
                    background: none;
                }
            }
        }

        &__login-section {
            text-align: center;
        }
        .gw-empty-state .gw-empty-state__icon {
            margin: 0;
        }
        .gw-empty-state .gw-empty-state__title {
            font-size: 14px;
            line-height: 16px;
        }

        .gw-empty-state .gw-btn.gw-button {
            font-weight: 600;
            color: var(--gw-gr-primary-link-color);
        }
    }
</style>
